import { forwardRef, useEffect, useRef, useState } from 'react';
import type { PropsWithoutRef, Ref } from 'react';
import * as QRCodeLib from 'qrcode';
import styled, { css } from 'styled-components';

import type { BaseProps, ForwardRefForwardPropsComponent } from '../../types';
import { defaultThemeProp } from '../../theme';
import Flex from '../Flex';
import Image from '../Image';
import { useI18n } from '../../hooks';
import EmptyState from '../EmptyState';

export const StyledEmptyQRWrapper = styled.div(({ theme }) => {
  return css`
    height: ${theme.base['content-width'].xs};
    width: ${theme.base['content-width'].xs};
    border: 0.0625rem solid ${theme.base.palette['border-line']};
    border-radius: calc(${theme.base['border-radius']} / 2);
    text-align: center;
  `;
});

StyledEmptyQRWrapper.defaultProps = defaultThemeProp;

export interface QRCodeProps extends BaseProps {
  /** Text that need to be encoded into QR code. */
  value: string;
  /** The HTML alt attribute for the image. */
  label?: string;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLImageElement | HTMLDivElement>;
}

const QRCode: ForwardRefForwardPropsComponent<QRCodeProps> = forwardRef(function QRCode(
  { value, label = value, ...restProps }: PropsWithoutRef<QRCodeProps>,
  ref: QRCodeProps['ref']
) {
  const [dataURL, setDataURL] = useState('');
  const t = useI18n();

  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    QRCodeLib.toDataURL(value)
      .then(results => {
        if (mounted.current) setDataURL(results);
      })
      .catch(() => {
        setDataURL('');
      });
  }, [value]);

  return dataURL ? (
    <Image {...restProps} ref={ref as Ref<HTMLImageElement>} src={dataURL} alt={label} />
  ) : (
    <Flex
      {...restProps}
      ref={ref}
      container={{
        direction: 'column',
        alignItems: 'center',
        justify: 'center',
        gap: 1,
        pad: 2
      }}
      as={StyledEmptyQRWrapper}
    >
      <EmptyState message={t('qr_code_not_available')} />
    </Flex>
  );
});

export default QRCode;
